import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// assets
import { UploadIllustration } from 'src/assets/illustrations';
//
import Iconify from '../iconify';
//
import RejectionFiles from './errors-rejection-files';
import MultiFilePreview from './preview-multi-file';
import SingleFilePreview from './preview-single-file';
import WebcamDialog from './webcam-dialog';

// ----------------------------------------------------------------------

export default function Upload({
  disabled,
  multiple = false,
  error,
  helperText,
  //
  file,
  onDelete,
  //
  files,
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  sx,
  uploadSource,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const webcamDialog = useBoolean();
  const [hasCamera, setHasCamera] = useState(false);

  useEffect(() => {
    async function checkCamera() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setHasCamera(devices.some((d) => d.kind === 'videoinput'));
      } catch (err) {
        setHasCamera(false);
      }
    }
    checkCamera();
  }, []);

  const handleCapture = (capturedFile) => {
    if (other.onDrop) {
      other.onDrop([capturedFile], []);
    }
  };

  const uploadInputProps = uploadSource === 'camera' || uploadSource === 'camera_location' 
    ? getInputProps({ capture: 'environment' }) 
    : getInputProps();

  const hasFile = !!file && !multiple;

  const hasFiles = !!files && multiple && !!files.length;

  const hasError = isDragReject || !!error;

  const isImageAccepted = Object.keys(other.accept || {}).some(key => key.includes('image'));
  const isVideoAccepted = Object.keys(other.accept || {}).some(key => key.includes('video'));

  let cameraButtonLabel = 'Take Photo';
  if (isImageAccepted && isVideoAccepted) {
    cameraButtonLabel = 'Take Photo / Video';
  } else if (isVideoAccepted) {
    cameraButtonLabel = 'Record Video';
  }

  const renderPlaceholder = (
    <Stack spacing={3} alignItems="center" justifyContent="center" flexWrap="wrap">
      <UploadIllustration sx={{ width: 1, maxWidth: 200 }} />
      <Stack spacing={1} sx={{ textAlign: 'center' }}>
        <Typography variant="h6">Drop or Select file</Typography>
        {(!uploadSource || uploadSource === 'file' || uploadSource === 'any') && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Drop files here or click
            <Box
              component="span"
              sx={{
                mx: 0.5,
                color: 'primary.main',
                textDecoration: 'underline',
              }}
            >
              browse
            </Box>
            thorough your machine
          </Typography>
        )}

        {(uploadSource === 'camera' || uploadSource === 'camera_location' || uploadSource === 'any') && 
          hasCamera && 
          (isImageAccepted || isVideoAccepted) && (
          <Button
            variant="soft"
            color="primary"
            startIcon={<Iconify icon="solar:camera-bold" />}
            onClick={(e) => {
              e.stopPropagation();
              webcamDialog.onTrue();
            }}
            sx={{ mt: 1 }}
          >
            {cameraButtonLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const renderSinglePreview = (
    <SingleFilePreview 
      file={file}
      imgUrl={typeof file === 'string' ? file : file?.preview} 
    />
  );

  const removeSinglePreview = hasFile && onDelete && (
    <IconButton
      size="small"
      onClick={onDelete}
      sx={{
        top: 16,
        right: 16,
        zIndex: 9,
        position: 'absolute',
        color: (theme) => alpha(theme.palette.common.white, 0.8),
        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
        },
      }}
    >
      <Iconify icon="mingcute:close-line" width={18} />
    </IconButton>
  );

  const renderMultiPreview = hasFiles && (
    <>
      <Box sx={{ my: 3 }}>
        <MultiFilePreview files={files} thumbnail={thumbnail} onRemove={onRemove} />
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
        {onRemoveAll && (
          <Button color="inherit" variant="outlined" size="small" onClick={onRemoveAll}>
            Remove All
          </Button>
        )}

        {onUpload && (
          <Button
            size="small"
            variant="contained"
            onClick={onUpload}
            startIcon={<Iconify icon="eva:cloud-upload-fill" />}
          >
            Upload
          </Button>
        )}
      </Stack>
    </>
  );

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        onClick={(e) => {
          if (
            (uploadSource === 'camera' || uploadSource === 'camera_location') &&
            hasCamera &&
            (isImageAccepted || isVideoAccepted)
          ) {
            e.stopPropagation();
            webcamDialog.onTrue();
          } else {
            getRootProps().onClick(e);
          }
        }}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72,
          },
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            color: 'error.main',
            borderColor: 'error.main',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
          }),
          ...(hasFile && {
            padding: '24% 0',
          }),
        }}
      >
        <input {...uploadInputProps} />

        {hasFile ? renderSinglePreview : renderPlaceholder}
      </Box>

      {removeSinglePreview}

      {helperText && helperText}

      <RejectionFiles fileRejections={fileRejections} />

      {renderMultiPreview}

      <WebcamDialog 
        open={webcamDialog.value}
        onClose={webcamDialog.onFalse}
        onCapture={handleCapture}
        uploadSource={uploadSource}
        accept={other.accept}
      />
    </Box>
  );
}

Upload.propTypes = {
  disabled: PropTypes.object,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  files: PropTypes.array,
  helperText: PropTypes.object,
  multiple: PropTypes.bool,
  onDelete: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onUpload: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
  uploadSource: PropTypes.string,
};
